//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { QueryRenderer, ReadyState } from "react-relay";
// event-scheduler-time-input-renderer imports
import { query } from "./EventSchedulerTimeInputRenderer.gql";
import { IRendererProps, IRendererContainer, IRendererState } from "./EventSchedulerTimeInput.d";
import { RoomOptionEnum } from "@components/event-scheduler";
import { EventSchedulerTimeInput } from "./EventSchedulerTimeInput";
import * as s from "./EventSchedulerTimeInput.styles";
// other internals
import { logger } from "@util/logger.util";
import { handleReadyStateError } from "@util/relay.util";
import { IReduxState } from "@state/reducers/root.reducer";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";

//////////////////
// COMPONENT
//////////////////
class EventSchedulerTimeInputRendererComponent extends React.PureComponent<
  IRendererProps,
  IRendererState
> {
  private cachedOrg: ISchema.IOrg | null = null;

  constructor(props: IRendererProps) {
    super(props);
    this.state = {
      boundaryError: false,
    };
  }

  public componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error("componentDidCatch in EventSchedulerTimeInputRenderer", error, info);

    // Display fallback UI
    this.setState({ boundaryError: true });
  }

  public render() {
    const {
      orgId,
      invitees,
      minutesDuration,
      timeZone,
      minSlot,
      maxSlot,
      includeRoom,
      roomsOverride,
      limit,
      seenSlots,
      traits,
    } = this.props;

    const inputQuery = {
      invitees,
      minutesDuration,
      timeZone,
      minSlot,
      maxSlot,
      roomsOverride,
      limit,
      seenSlots,
      includeRoom: includeRoom !== RoomOptionEnum.WithoutRoom,
      traits,
    };

    let externalEventId = "";
    if (this.props.reschedulerEvent) {
      externalEventId = this.props.reschedulerEvent.eventKey.externalEventId;
    }

    return (
      <QueryRenderer
        environment={this.props.environment}
        query={query}
        variables={{
          orgRelayId: orgId,
          query: inputQuery,
          rescheduler: !!this.props.reschedulerEvent,
          reschedulerExternalEventId: externalEventId,
          reschedulerTimeZone: timeZone,
          reschedulerRangeMin: minSlot,
          reschedulerRangeMax: maxSlot,
        }}
        cacheConfig={{}}
        render={(readyState: ReadyState) => {
          const freshOrg = readyState.props && readyState.props.node;
          const loadingForTheFirstTime = !this.cachedOrg && !freshOrg;

          // check for error or show a loader for first load
          if (readyState.error) {
            handleReadyStateError(readyState.error, "EventSchedulerTimeInputRenderer");

            return <div>An error has occurred. Please try refreshing the page</div>;
          }

          if (loadingForTheFirstTime) {
            // loading for the first time
            return (
              <div
                style={
                  this.props.open
                    ? {
                        ...s.editModeContainer,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }
                    : {
                        padding: "4px 0",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }
                }
              >
                <CircularProgress />
              </div>
            );
          }

          // cache an org so it doesn't flash on new loads
          if (freshOrg) {
            this.cachedOrg = freshOrg;
          }

          const org = freshOrg || this.cachedOrg;
          const schedulingResponseErrorable =
            org.schedulingResponseErrorable || org.reschedulingResponseErrorable;

          return (
            <EventSchedulerTimeInput
              schedulingResponseErrorable={schedulingResponseErrorable}
              org={org}
              onChange={this.props.onChange}
              includeRoom={includeRoom}
              roomsOverride={roomsOverride}
              minutesDuration={minutesDuration}
              selectedSlot={this.props.selectedSlot}
              range={this.props.range}
              specifiedDate={this.props.specifiedDate}
              open={this.props.open}
              persons={this.props.persons}
              timeError={this.props.timeError}
              useAutoSchedule={this.props.useAutoSchedule}
              invitees={this.props.invitees}
              showCloseButton={this.props.showCloseButton}
              isLoading={this.props.isLoading}
            />
          );
        }}
      />
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IRendererContainer) {
  return {
    reschedulerEvent: state.feedScheduler.reschedulerEvent,
  };
}

export const EventSchedulerTimeInputRenderer = connect(
  mapStateToProps,
  {},
)(EventSchedulerTimeInputRendererComponent);
